import React, { useEffect, useRef } from 'react';

/**
 * CursorLens — forme polarisante à bord vivant autour du curseur
 * (inspiration Chris Kalafatis).
 *
 * Le curseur natif est conservé. Une forme suit la souris et passe le contenu
 * sous elle en négatif noir & blanc (backdrop-filter) — l'INTÉRIEUR reste net.
 * Seul le CONTOUR est distordu (bruit de Perlin appliqué au MASQUE).
 *
 * PERF / FLUIDITÉ : le bruit feTurbulence est STATIQUE (calculé une seule fois,
 * mis en cache). On n'anime que des paramètres légers :
 *   - feDisplacementMap.scale (sinusoïde douce + vitesse) -> bords qui ondulent
 *   - transforms GPU (suivi + squash/stretch) -> traînée
 * Aucune régénération de bruit par frame -> animation fluide.
 *
 * Désactivé sur tactile et en prefers-reduced-motion. pointer-events: none.
 */
const HOVER_SELECTOR =
  'a, button, img, [role="button"], .project-card, .card, [data-cursor-lens]';

const BASE_SCALE = 32; // ondulation des bords au repos
const RIPPLE = 18; // amplitude de la vague principale
const RIPPLE2 = 10; // amplitude de la 2e vague (déphasée) -> houle plus marquée
const SPEED_SCALE = 8; // léger surplus d'ondulation par la vitesse
const MAX_SCALE = 75; // borne haute de sécurité (au-dessus du pic de repos ~60,
                      // n'écrête donc pas les vagues -> reste fluide)
const STRETCH = 0.5; // intensité de la traînée (allongement)

const CursorLens = () => {
  // Skip rendering entirely on touch-only devices
  const isTouchOnly = typeof window !== 'undefined' &&
    window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  if (isTouchOnly) return null;

  const ref = useRef(null);
  const posRef = useRef(null);
  const dispRef = useRef(null);
  const stretchRef = useRef(null);

  useEffect(() => {
    const el = ref.current;
    const pos = posRef.current;
    const disp = dispRef.current;
    const stretchEl = stretchRef.current;
    if (!el || !pos || !disp || !stretchEl) return;

    // Uniquement sur les pointeurs fins (souris/trackpad), pas en tactile
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let frame = null;
    let running = false;

    let targetX = window.innerWidth / 2;
    let targetY = window.innerHeight / 2;
    let curX = targetX;
    let curY = targetY;
    let prevX = curX; // position lissée de la frame précédente
    let prevY = curY;
    let speed = 0; // 0 -> 1, dérivé de la position LISSÉE (donc sans jitter)
    let dirX = 0; // direction de déplacement lissée
    let dirY = 0;
    let angle = 0; // angle d'étirement, conservé entre frames

    const render = (now) => {
      const t = now / 1000;

      // --- Suivi du curseur (traîne douce) — transform direct (GPU) ---
      curX += (targetX - curX) * 0.18;
      curY += (targetY - curY) * 0.18;
      pos.style.transform = `translate3d(${curX}px, ${curY}px, 0)`;

      // --- Vitesse & direction dérivées de la position LISSÉE -> pas de jitter ---
      const vx = curX - prevX;
      const vy = curY - prevY;
      prevX = curX;
      prevY = curY;
      const inst = Math.hypot(vx, vy); // px/frame, déjà lissé par la traîne
      speed += (Math.min(inst / 22, 1) - speed) * 0.15; // lissage supplémentaire
      dirX += (vx - dirX) * 0.12;
      dirY += (vy - dirY) * 0.12;
      if (speed > 0.03) angle = Math.atan2(dirY, dirX); // fige l'angle au quasi-arrêt

      // --- Ondulation des bords : 2 vagues déphasées + vitesse (bruit statique) ---
      const sc =
        BASE_SCALE +
        RIPPLE * Math.sin(t * 2.4) +
        RIPPLE2 * Math.sin(t * 1.55 + 1.1) +
        speed * SPEED_SCALE;
      // Bornée pour ne jamais sur-déplacer (sinon le motif se déchire)
      disp.setAttribute('scale', Math.min(MAX_SCALE, Math.max(2, sc)).toFixed(1));

      // --- Traînée : étirement dans la direction du mouvement ---
      const stretch = speed * STRETCH;
      stretchEl.style.transform =
        `rotate(${angle}rad) scale(${(1 + stretch).toFixed(3)}, ${(1 - stretch * 0.6).toFixed(3)})`;

      frame = requestAnimationFrame(render);
    };

    const start = () => {
      if (!running) {
        running = true;
        frame = requestAnimationFrame(render);
      }
    };
    const stop = () => {
      running = false;
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
    };

    const handleMove = (e) => {
      // On ne fait que poser la cible : vitesse/direction sont calculées dans
      // render() à partir de la position lissée (évite tout jitter).
      targetX = e.clientX;
      targetY = e.clientY;
      if (!el.classList.contains('is-active')) el.classList.add('is-active');
      start();
    };

    const handleOver = (e) => {
      if (e.target.closest && e.target.closest(HOVER_SELECTOR)) {
        el.classList.add('is-hover');
      }
    };

    const handleOut = (e) => {
      const to = e.relatedTarget;
      if (!to || !(to.closest && to.closest(HOVER_SELECTOR))) {
        el.classList.remove('is-hover');
      }
    };

    const handleLeave = () => {
      el.classList.remove('is-active', 'is-hover');
      stop();
    };

    const handleDown = () => el.classList.add('is-down');
    const handleUp = () => el.classList.remove('is-down');

    window.addEventListener('pointermove', handleMove, { passive: true });
    document.addEventListener('pointerover', handleOver, { passive: true });
    document.addEventListener('pointerout', handleOut, { passive: true });
    document.addEventListener('pointerdown', handleDown, { passive: true });
    document.addEventListener('pointerup', handleUp, { passive: true });
    document.documentElement.addEventListener('pointerleave', handleLeave);

    return () => {
      window.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerover', handleOver);
      document.removeEventListener('pointerout', handleOut);
      document.removeEventListener('pointerdown', handleDown);
      document.removeEventListener('pointerup', handleUp);
      document.documentElement.removeEventListener('pointerleave', handleLeave);
      stop();
    };
  }, []);

  return (
    <div className="cursor-lens" ref={ref} aria-hidden="true">
      {/*
        Masque organique : disque plein dont SEUL LE BORD est distordu par le
        bruit de Perlin (STATIQUE) -> intérieur net, bords qui ondulent via
        l'amplitude du déplacement (animée, légère).
      */}
      <svg className="cursor-lens__defs" width="0" height="0" aria-hidden="true">
        <defs>
          <filter
            id="cursorEdge"
            x="-40%"
            y="-40%"
            width="180%"
            height="180%"
            primitiveUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.016 0.02"
              numOctaves="2"
              seed="11"
              stitchTiles="stitch"
              result="noise"
            />
            <feDisplacementMap
              ref={dispRef}
              in="SourceGraphic"
              in2="noise"
              scale={BASE_SCALE}
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>

          <mask
            id="cursorBlobMask"
            maskUnits="userSpaceOnUse"
            maskContentUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="360"
            height="360"
          >
            <circle cx="180" cy="180" r="104" fill="#ffffff" filter="url(#cursorEdge)" />
          </mask>
        </defs>
      </svg>

      <div className="cursor-lens__blob-pos" ref={posRef}>
        <div className="cursor-lens__blob-scale">
          <div className="cursor-lens__blob-stretch" ref={stretchRef}>
            <div className="cursor-lens__blob" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CursorLens;
